import { BaseComponentService } from 'src/app/services/_app/base-component.service';
import { PurchasesTracesService } from 'src/app/services/purchases-traces.service';
import { PurchaseTrace } from 'src/app/models/accounting/purchase-trace.model';
import { ApiPagination } from 'src/app/models/api/api-pagination.model';
import { ScholasticYear } from 'src/app/models/scholastic-year.model';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ApiError } from 'src/app/models/api/api-error.model';
import { AuthService } from 'src/app/services/auth.service';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, Subscription } from 'rxjs';
import { API } from 'src/app/constants/api';
import * as moment from 'moment';

@Component({
  selector: 'app-purchases-traces',
  templateUrl: './purchases-traces.component.html',
})
export class PurchasesTracesComponent implements OnInit, OnDestroy {
  private _authService = inject(AuthService);
  private _baseComponentService = inject(BaseComponentService);
  private _purchasesTracesService: PurchasesTracesService = inject(
    PurchasesTracesService
  );

  protected _subscription?: Subscription;
  protected downloadingCsv: boolean = false;
  protected pagination: ApiPagination = new ApiPagination();
  protected purchasesTraces: PurchaseTrace[] = [];
  protected loading: boolean = true;
  protected error?: ApiError;
  private _txt: string = '';

  protected sectionsState$ = this._authService.sectionsState$;
  protected schoolYears$ = this._purchasesTracesService.schoolYears$;
  protected categoriesOptions$ =
    this._purchasesTracesService.categoriesOptions$;
  protected getColor(category: string): string {
    return this._purchasesTracesService.getCategoryColor(category);
  }

  protected filterForm: FormGroup = new FormGroup({
    type: new FormControl('range'),
    year: new FormControl(new Date().getFullYear()),
    school_year: new FormControl(ScholasticYear.getCurrent()),
    from: new FormControl(moment().startOf('month').format('YYYY-MM-DD')),
    to: new FormControl(moment().endOf('month').format('YYYY-MM-DD')),
    category: new FormControl(null),
  });

  ngOnInit(): void {
    this._getData();
    this._subscription = this.filterForm.valueChanges
      .pipe(debounceTime(800))
      .subscribe(() => this._getData());
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }

  protected onText(text: string) {
    this._txt = text;
    this._getData();
  }

  protected onPage(page: number): void {
    if (this.pagination) {
      this.pagination.page = page;
      this._getData();
    }
  }

  private get _getFilterObj(): {
    year?: string;
    from?: string;
    to?: string;
    txt?: string;
    category?: string;
    school_year?: string;
  } {
    let type: 'year' | 'school_year' | 'range' = 'year';
    switch (this.filterForm.get('type')?.value) {
      case 'year':
        type = 'year';
        break;
      case 'school_year':
        type = 'school_year';
        break;
      case 'range':
        type = 'range';
        break;
    }
    return Object.fromEntries(
      Object.entries({
        year: type === 'year' ? this.filterForm.get('year')?.value : undefined,
        from: type === 'range' ? this.filterForm.get('from')?.value : undefined,
        to: type === 'range' ? this.filterForm.get('to')?.value : undefined,
        txt: this._txt.length > 0 ? this._txt : undefined,
        school_year:
          type === 'school_year'
            ? this.filterForm.get('school_year')?.value
            : undefined,
        category:
          this.filterForm.get('category')?.value !== null
            ? this.filterForm.get('category')?.value.toUpperCase().trim()
            : undefined,
      }).filter(([_, v]) => v !== undefined)
    );
  }

  private async _getData() {
    this.loading = true;
    this.error = undefined;
    this._purchasesTracesService
      .getPurchasesTracesListing(
        Object.assign(this._getFilterObj, this.pagination)
      )
      .subscribe({
        next: (response) => {
          this.purchasesTraces = response.data ?? [];
          this.pagination = response.pagination ?? new ApiPagination();
        },
        error: (error) => {
          this.error = error;
          console.error('getPurchasesTracesListing | Error:', error);
        },
      })
      .add(() => (this.loading = false));
  }

  // Download Attachment
  protected downloadAttachment(purchaseTrace?: PurchaseTrace) {
    this._baseComponentService.downloadFile({
      withCredentials: true,
      url: API.purchaseTraceAttachment(
        purchaseTrace?.id,
        purchaseTrace?.attachment
      ),
    });
  }

  // Delete Attachment
  protected deletePurchaseTraceAttachment(purchaseTrace?: PurchaseTrace) {
    if (!purchaseTrace?.attachment) return;
    this._baseComponentService
      .confirmDeletion({
        message: 'Confermi di voler eliminare questo allegato?',
        details: "Attenzione: l'operazione non sarà in alcun modo reversibile",
        falseBtn: 'No, annulla',
        trueBtn: 'Sì, elimina',
        destructive: true,
        size: '450px',
      })
      .subscribe((response) => {
        if (response === true) {
          this._purchasesTracesService
            .deletePurchaseTraceAttachment(purchaseTrace)
            .subscribe({
              next: () => this._getData(),
              error: (error: ApiError) => {
                console.error(
                  'Delete Purchase Trace Attachment Error: ',
                  error
                );
                this._baseComponentService.showErrorDialog({ error: error });
              },
            });
        }
      });
  }

  // Update CheckBox Value
  protected onChangeInvoiceReceivedCheckbox(id?: string): void {
    if (!id) return;
    const trace = this.purchasesTraces.find((pt) => pt.id === id);
    if (trace !== undefined && typeof trace.invoiceReceived === 'boolean') {
      this._baseComponentService
        .confirmDeletion({
          message: `Confermi di voler registrare la ricezione della fattura per l'acquisto tracciato con il DEA-ID ${trace.dea_id}?`,
          details:
            'ATTENZIONE: una volta confermata la ricezione della fattura non sarà più possibile modificare questo elemento, inclusa la spunta di ricezione fattura che si sta modificando in questo momento',
          falseBtn: 'No, annulla',
          trueBtn: 'Sì, conferma',
          destructive: false,
          size: '500px',
        })
        .subscribe((confirmed: boolean) => {
          if (confirmed === true) {
            const newValue = trace.invoiceReceived === false ? true : false; // !
            this._purchasesTracesService
              .patchPurchaseTrace(id, { invoiceReceived: newValue })
              .subscribe({
                next: (response) => {
                  const index = this.purchasesTraces.findIndex(
                    (pt) => pt.id === id
                  );
                  if (index >= 0 && response.data) {
                    this.purchasesTraces[index] = response.data;
                  }
                },
                error: (error) => {
                  console.error(
                    'onChangeInvoiceReceivedCheckbox ERROR:',
                    error
                  );
                  this._baseComponentService.showErrorDialog({ error });
                  if (trace && typeof trace.invoiceReceived == 'boolean') {
                    trace.invoiceReceived = !trace.invoiceReceived;
                    setTimeout(
                      () => (trace.invoiceReceived = !trace.invoiceReceived),
                      500
                    );
                  }
                },
              });
          } else {
            const index = this.purchasesTraces.findIndex((pt) => pt.id === id);
            if (index >= 0) {
              this.purchasesTraces[index].invoiceReceived =
                !this.purchasesTraces[index].invoiceReceived;
              setTimeout(() => {
                this.purchasesTraces[index].invoiceReceived =
                  !this.purchasesTraces[index].invoiceReceived;
              }, 200);
            }
          }
        });
    }
  }

  // Edit Block
  protected editPurchaseTrace(id?: string): void {
    if (!id) return;
    this._purchasesTracesService
      .openPurchaseTraceDialog({ id })
      .afterClosed()
      .subscribe((response) => {
        if (response === true) this._getData();
      });
  }

  // Delete Block
  protected deletePurchaseTrace(id?: string): void {
    if (!id) return;
    this._baseComponentService
      .confirmDeletion({
        message:
          'Confermi di voler eliminare il tracciamento di questo acquisto?',
        details:
          "Attenzione: oltre al tracciamento dell'acquisto, sarà eliminato anche l'eventuale allegato. Le operazioni non saranno in alcun modo reversibili.",
        falseBtn: 'No, annulla',
        trueBtn: 'Sì, elimina',
        destructive: true,
        size: '450px',
      })
      .subscribe((response) => {
        if (response === true) {
          this._purchasesTracesService.deletePurchaseTrace(id).subscribe({
            next: () => this._getData(),
            error: (error: ApiError) => {
              console.error('Delete Purchase Trace Error: ', error);
              this._baseComponentService.showErrorDialog({ error: error });
            },
          });
        }
      });
  }

  // Get CSV
  protected async downloadCSV() {
    this.downloadingCsv = true;
    this._baseComponentService
      .downloadFile({
        url: API.purchasesTracesCSV(),
        withCredentials: true,
        params: this._getFilterObj,
      })
      .finally(() => (this.downloadingCsv = false));
  }
}
